import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { Logger } from '@curbnturf/network';
import { CroppieOptions } from 'croppie';
import { CroppingComponent } from '../cropping/cropping.component';
import { ImageUploadOptions } from '../image-upload-options';

@Component({
  selector: 'curbnturf-image-uploader-base',
  templateUrl: './image-uploader-base.component.html',
  styleUrls: ['./image-uploader-base.component.scss'],
  standalone: true,
  imports: [FormsModule, CroppingComponent, MiscSvgComponent],
})
export class ImageUploaderBaseComponent implements OnInit {
  @ViewChild('cropper') cropper: CroppingComponent;

  @Input() options: ImageUploadOptions;

  @Input() croppieImage: string;
  @Input() originalBlob: Blob;
  @Input() maxDimensions: { width: number; height: number } | undefined;
  @Input() submitButtonText: string = 'Upload';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() save: EventEmitter<FormData> = new EventEmitter();
  @Output() close: EventEmitter<void> = new EventEmitter();

  widthPx = '200';
  heightPx = '200';
  croppiedImage?: string;
  // Cropping points array, the order is right, top, left, bottom.
  croppingPoints: { right: number; top: number; left: number; bottom: number };
  zoomRatio: number;
  caption: string;
  rotation: number = 0;

  timeout: boolean;

  get heightRatio(): string {
    if (!this.options) {
      return '0';
    }

    if (this.options?.croppieOptions?.boundary) {
      return `${this.options.croppieOptions.boundary.height}px`;
    }
    if (this.options?.output?.size) {
      const size: 'viewport' | 'original' | { height: number; width: number } = this.options.output.size;

      if (typeof size !== 'string' && size.height && size.width) {
        return `${(size.height / size.width) * 100}%`;
      }
    }

    return '0';
  }

  get widthRatio(): string {
    if (!this.options) {
      return '0';
    }

    if (this.options?.croppieOptions?.boundary) {
      return `${this.options.croppieOptions.boundary.width}px`;
    }
    if (this.options?.output?.size) {
      const size: 'viewport' | 'original' | { height: number; width: number } = this.options.output.size;

      if (typeof size !== 'string' && size.height && size.width) {
        return `${(size.width / size.height) * 100}%`;
      }
    }

    return '0';
  }

  get croppieOptions(): CroppieOptions {
    const opts: CroppieOptions = {};
    opts.enableOrientation = true;
    opts.showZoomer = true;
    return { ...opts, ...this.options?.croppieOptions };
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private logger: Logger,
  ) {}

  ngOnInit() {
    setTimeout(() => {
      this.timeout = true;
    }, 100);
  }

  newImageResultFromCroppie(img: { blob: Blob | string; points: number[]; ratio: number }) {
    this.croppiedImage = img.blob as string;
    this.croppingPoints = {
      left: img.points[0],
      top: img.points[1],
      right:
        !this.maxDimensions || !this.maxDimensions.width
          ? img.points[2]
          : this.maxDimensions?.width > 0 && img.points[2] < this.maxDimensions?.width
            ? img.points[2]
            : this.maxDimensions?.width || 0,
      bottom:
        !this.maxDimensions || !this.maxDimensions.height
          ? img.points[3]
          : this.maxDimensions?.height > 0 && img.points[3] < this.maxDimensions?.height
            ? img.points[3]
            : this.maxDimensions?.height || 0,
    };
    // Remove one pixel from the bottom of the crop to ensure we make it within crop.
    this.croppingPoints.bottom = this.croppingPoints.bottom <= 0 ? 0 : this.croppingPoints.bottom - 1;
    // Remove one pixel from the right of the crop to ensure we make it within crop.
    this.croppingPoints.right = this.croppingPoints.right <= 0 ? 0 : this.croppingPoints.right - 1;
    this.zoomRatio = img.ratio;
    this.cdr.detectChanges();
  }

  async saveFunction() {
    if (this.croppiedImage) {
      const files = this.fileFormatter([this.originalBlob]);
      this.save.emit(files);
    } else {
      this.logger.warning('Attempted to save an image, but image data was not provided.');
    }
  }

  cancelCroppieEdit() {
    delete this.croppiedImage;
    this.close.emit();
    this.cdr.detectChanges();
  }

  rotateDir(dir: string) {
    // dir: direction = {'l', 'r'}
    if (!this.croppieImage) {
      return;
    }
    const rotateLeft = 90;
    const rotateRight = -90;
    this.rotation += dir === 'l' ? rotateLeft : rotateRight;
    this.rotation = this.rotation % 360;
    if (dir === 'l') {
      this.cropper.rotate(rotateLeft);
      return;
    }
    this.cropper.rotate(rotateRight);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  imageUploadEvent(evt: any) {
    if (!evt.target) {
      return;
    }
    if (!evt.target.files) {
      return;
    }
    if (evt.target.files.length !== 1) {
      return;
    }
    const file = evt.target.files[0];
    if (file.type !== 'image/jpeg' && file.type !== 'image/png' && file.type !== 'image/jpg') {
      return;
    }
    const fr = new FileReader();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fr.onloadend = (loadEvent: any) => {
      this.croppieImage = loadEvent.target.result;
      this.cdr.detectChanges();
    };
    fr.readAsDataURL(file);
  }

  protected fileFormatter(files: Blob[]): FormData {
    const filesFormData = new FormData();

    for (const file of files) {
      filesFormData.append('files', file);
      filesFormData.append('caption', this.caption || '');
      filesFormData.append('cropLeft', this.croppingPoints.left.toString() || '');
      filesFormData.append('cropTop', this.croppingPoints.top.toString() || '');
      filesFormData.append('cropRight', this.croppingPoints.right.toString() || '');
      filesFormData.append('cropBottom', this.croppingPoints.bottom.toString() || '');
      filesFormData.append('zoom', this.zoomRatio.toString() || '');
      filesFormData.append('rotation', this.rotation.toString() || '0');
      if (this.options.data) {
        const data = this.options.data;
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            filesFormData.append(key, data[key]);
          }
        }
      }
    }

    return filesFormData;
  }
}
